/**
 * What a formula computes, and how the answer reads.
 *
 * A formula field has no type of its own. The backend works one out from the
 * expression, and everything below the box is the configuration of *that* type
 * -- so a formula returning a duration is offered a duration format, in the same
 * control a duration field is.
 *
 * Nothing here knows which formatting belongs to which formula type. The typing
 * endpoint serialises a whole unsaved field, so its answer already carries the
 * properties the backend would give it; this seeds the draft from that answer
 * whenever the expression starts producing a different kind of value, and leaves
 * it alone afterwards so a chosen format survives the next keystroke.
 */
import { Suspense, lazy, useRef, useState } from 'react'

import type { FieldPropertiesOf } from '@/api/types'
import { Skeleton } from '@/shared/ui/skeleton'
import { FORMULA_TYPE_BEHAVES_AS, isArrayFormulaType } from '../../formula-types'
import type { FormulaTyping } from '../../../formula/type-formula'
import {
  DateFormatting,
  DurationFormatting,
  NumberFormatting,
  changing,
  type DateFormattingProperties,
  type DurationFormattingProperties,
  type NumberFormattingProperties,
} from './formatting'
import type { SubForm } from './types'

/**
 * Loaded on demand, which is what the plan means by the formula editor being a
 * lazy chunk: a formula field is a minority of fields, this form is only ever
 * reached deliberately, and the language reference it carries does not belong in
 * the bundle everybody downloads to look at a grid.
 */
const FormulaEditor = lazy(async () => {
  const module = await import('../../../formula/formula-editor')
  return { default: module.FormulaEditor }
})

/** The field type a formula's result behaves like, entries and all. */
function behavesAs(typing: FormulaTyping | null, stored: unknown): string | null {
  const formulaType = typing?.type ?? (typeof stored === 'string' ? stored : null)
  if (formulaType === null) return null
  const entry = formulaType === 'array' ? typing?.arrayType : formulaType
  return isArrayFormulaType(entry) ? FORMULA_TYPE_BEHAVES_AS[entry] : null
}

export const FormulaForm: SubForm<'formula'> = ({
  properties,
  onChange,
  field,
  tableId,
  name,
}) => {
  // What the backend last said. In state rather than a ref because it decides
  // which formatting is shown, so an answer arriving has to redraw.
  const [typing, setTyping] = useState<FormulaTyping | null>(null)
  // Which kind of value the draft has already been seeded for, so that seeding
  // happens on a change of kind rather than on every answer.
  const seededFor = useRef<string | null>(null)

  const stored = (field as { formula_type?: unknown }).formula_type
  const kind = behavesAs(typing, stored)

  return (
    <>
      <Suspense fallback={<Skeleton className="h-28 w-full" />}>
        <FormulaEditor
          tableId={tableId}
          fieldId={field.id}
          name={name}
          formula={properties.formula}
          onFormula={(formula) => {
            onChange({ formula })
          }}
          onTyping={(answer) => {
            setTyping(answer)
            const now = answer?.type ?? null
            // Only when the kind of value changes, including the first answer
            // about a formula that is being written from nothing. Seeding on
            // every answer would put the backend's defaults back over a format
            // somebody had just chosen, one keystroke later.
            if (now !== null && now !== seededFor.current) {
              seededFor.current = now
              // Not narrowed on the way in: `FORMULA_FORMATTING_KEYS` is what
              // decides which properties an answer is read for, and it is
              // checked against the type there.
              onChange(answer?.formatting ?? {})
            }
          }}
        />
      </Suspense>

      {kind === 'number' && (
        <NumberFormatting
          properties={properties}
          onChange={changing<
            NumberFormattingProperties,
            Partial<FieldPropertiesOf<'formula'>>
          >(onChange)}
        />
      )}
      {kind === 'date' && (
        <DateFormatting
          properties={properties}
          // A formula's dates are computed on every read rather than stored, so
          // there is nothing to move when the zone changes.
          convertingFrom={undefined}
          onChange={changing<
            DateFormattingProperties,
            Partial<FieldPropertiesOf<'formula'>>
          >(onChange)}
        />
      )}
      {kind === 'duration' && (
        <DurationFormatting
          properties={properties}
          onChange={changing<
            DurationFormattingProperties,
            Partial<FieldPropertiesOf<'formula'>>
          >(onChange)}
        />
      )}
    </>
  )
}
